// types/chatBlocks.ts
//
// What a `block` event's `payload` carries, per kind (ORA-435 spec, "Served
// content"). ora-be ADR-0006 types the envelope — `blockId`, `kind`, `placement`,
// `target` — and leaves `payload` opaque; this file is the other half of that
// contract, between `ora-be`'s serve tools (ORA-442) and the frontend block
// registry.
//
// Conventions, so the backend has no guessing to do:
//
// - camelCase, like the rest of the stream protocol.
// - A payload carries the content the block renders. Read tools return ids and
//   summaries; a serve block is the one place a content body reaches the user,
//   so the block must be renderable from the payload alone (a video and an
//   article additionally re-fetch, because they play and paginate).
// - Everything past the identifying id is optional. The parsers in
//   `chatBlockPayload.ts` are tolerant: a missing optional field degrades the
//   block, a missing required id makes it a placeholder, and nothing throws.
// - **Content strings are server-filled, never model-authored.** The question
//   stem and option text, the article and help-doc HTML, and the video URLs all
//   come from Ora's own content rows: the model chooses *which* ids to serve and
//   the serve tool fills the payload from the database. The client renders them
//   as trusted content (the same HTML pipelines QBank and the library use) only
//   under that rule, so a serve tool must never pass model-written text through
//   one of these fields.
// - `target` (ora-be ADR-0006) is a *named route id* plus params — never a path. The
//   vocabulary is `ChatRouteId` from `chatEnvelope.ts`, the same one route
//   handles publish, and `chatBlockTargets.ts` is the only place that turns one
//   into a URL.

import type { FlashcardScheduling } from './Flashcard';

/** The block kinds the registry can render. */
export const CHAT_BLOCK_KINDS = [
  'question',
  'flashcards',
  'video',
  'article',
  'help_doc',
  'dashboard',
  'document',
  'document_excerpt',
  'navigation',
  'study_set',
  'set_result',
  'file',
  'draft_question',
  // mcgraw-hill: a figure from a reference document, placed by a scripted answer.
  'mcgraw_hill_figure',
  // mcgraw-hill: a scripted answer's action row ("Open in Harrison's", Copy).
  'mcgraw_hill_actions',
] as const;

export type ChatBlockKind = (typeof CHAT_BLOCK_KINDS)[number];

export const isChatBlockKind = (kind: string): kind is ChatBlockKind =>
  (CHAT_BLOCK_KINDS as readonly string[]).includes(kind);

/** At most this many blocks are shown per turn, so a reply stays readable. */
export const MAX_CHAT_BLOCKS_PER_TURN = 3;

/** One answer option of a served question. */
export interface ChatQuestionBlockOption {
  /** A–E. Falls back to the option's position when the server omits it. */
  letter?: string;
  text: string;
  /** Why this option is right or wrong; shown after the student answers. */
  explanation?: string | null;
  /** Only consulted when the payload has no `correctLetter`. */
  isCorrect?: boolean;
}

/**
 * The free-tier daily QBank allowance as it stood when the block was served.
 * It rides on the block because a question answered in chat spends from the
 * same daily budget as one answered in QBank.
 */
export interface ChatQuestionBlockLimits {
  /** Questions left today, or null when the user is not capped. */
  qbankRemaining: number | null;
  qbankLimit: number | null;
}

export interface ChatQuestionBlockPayload {
  vignetteId: string;
  variantId?: string | null;
  stem: string;
  options: ChatQuestionBlockOption[];
  correctLetter?: string | null;
  /** Question-level explanation, used when the options carry none. */
  explanation?: string | null;
  limits?: ChatQuestionBlockLimits | null;
  /**
   * The letter the student already submitted for this vignette, when the server
   * knows one. It is the same fact answer visibility is derived from, so a
   * question the student has answered before is served already revealed rather
   * than inviting a second, spoiling attempt.
   */
  selectedLetter?: string | null;
  /** The lecture a document chat is grounded in; attributes the answer to it. */
  lectureId?: string | null;
  /**
   * The student's stored stem highlights for this variant, in QBank's format
   * (ORA-822). Null when there are none; absent on blocks served before the
   * field existed, which is not the same as "none".
   */
  questionHighlights?: string | null;
  /** The student's stored crossouts for this variant, by option in letter order. */
  answerCrossouts?: boolean[];
}

/** One answer option of a chat-authored draft question. */
export interface ChatDraftQuestionBlockOption {
  /** A–E, assigned by the server after it shuffled the options. */
  letter: string;
  text: string;
}

/** One option's explanation, keyed by the letter the server gave that option. */
export interface ChatDraftQuestionBlockExplanation {
  letter: string;
  text: string;
}

/**
 * A practice question Ora wrote itself (ORA-934), delivered as a draft.
 *
 * This payload is the ONE exception to the "content strings are server-filled,
 * never model-authored" rule above, and the exception is bounded on purpose:
 *
 * - The stem, the option text, the explanations and the learning objective were
 *   written by a model inside the `draft_practice_question` server tool's
 *   generation request, from a source the server read itself, then validated and
 *   sanitized there. The server stores them on a `ChatDraftQuestion` row and
 *   this payload is filled from that row, which is also how a reloaded thread
 *   gets its draft back.
 * - Because that text is model-written, the client renders it as TEXT. It never
 *   goes through `renderVignetteContent` / `parseHtmlWithMathLatex` or any other
 *   trusted-HTML path, and never through `dangerouslySetInnerHTML`. The only
 *   markup honoured is `<sup>` and `<sub>`, mapped to elements one tag at a time
 *   by `draftQuestionText.tsx`; everything else renders literally.
 * - A draft is not a library item: it has no `vignetteId`, writes nothing to
 *   QBank, spends no QBank allowance, and carries no explainer spans or images.
 *
 * Writing one takes longer than a turn may last (p50 30 s against a 45 s turn
 * deadline), so the block arrives BEFORE the question exists: the tool creates
 * the row and serves a `pending` payload, and the block itself asks the server
 * to generate, then renders the `ready` — or `failed` — payload that comes back.
 * `status` is the discriminator, and history re-hydration returns the payload
 * for the row's status at that moment, so all three states can arrive on reload.
 */
export type ChatDraftQuestionStatus = 'pending' | 'failed' | 'ready';

/** The draft exists and the question is still being written. */
export interface ChatDraftQuestionPendingPayload {
  draftId: string;
  status: 'pending';
  /** How the note names the source, e.g. `your lecture "Renal Physiology 3"`. */
  sourceLabel: string;
  /** What the student asked the question to test; not shown, but kept so the
   * pending state is a complete record of the request. */
  focus: string;
}

/** The question could not be written. One copy covers every reason. */
export interface ChatDraftQuestionFailedPayload {
  draftId: string;
  status: 'failed';
  sourceLabel: string;
  /**
   * `generation_failed`, `validation_failed`, `source_unavailable` or
   * `timed_out` today. Typed as a string because the student is never shown it
   * and a reason this client has not heard of must not break the block.
   */
  reason: string;
}

/** The question itself. `correctLetter` is in the payload because the reveal is
 * client-side off it, exactly as the vetted question block does it — nothing is
 * fetched when the student commits. */
export interface ChatDraftQuestionReadyPayload {
  draftId: string;
  status: 'ready';
  sourceLabel: string;
  questionType?: string | null;
  stem: string;
  options: ChatDraftQuestionBlockOption[];
  correctLetter: string;
  /** In option order; the block hoists the correct one when it reveals. */
  explanations: ChatDraftQuestionBlockExplanation[];
  /** The item's `keyPoint`, shown as the `Learning Objective:` line. */
  learningObjective: string;
  /** The letter the student already picked, so history reloads revealed. */
  selectedLetter?: string | null;
  /** The thumbs-down reason already recorded, so history reloads thanked. */
  feedbackReason?: string | null;
}

export type ChatDraftQuestionBlockPayload =
  | ChatDraftQuestionPendingPayload
  | ChatDraftQuestionFailedPayload
  | ChatDraftQuestionReadyPayload;

export interface ChatFlashcardBlockCard {
  id: string;
  front: string;
  back: string;
  explanation?: string | null;
}

export interface ChatFlashcardsBlockPayload {
  cards: ChatFlashcardBlockCard[];
}

export interface ChatVideoBlockPayload {
  videoId: string;
  title?: string | null;
  description?: string | null;
  thumbnailUrl?: string | null;
  /** Where the answer is; the player opens seeked to it. */
  startSecond?: number | null;
}

export interface ChatArticleBlockPayload {
  /** Article id or slug — `fetchArticle` accepts either. */
  articleId: string;
  title?: string | null;
  sectionId?: string | null;
}

export interface ChatHelpDocBlockPayload {
  slug: string;
  title?: string | null;
  /** The doc body. Absent means the block renders from the slug alone. */
  content?: string | null;
}

/** One row of the dashboard block's topic lists. */
export interface ChatDashboardTopic {
  name: string;
  /** Percent correct, 0–100. */
  accuracy?: number | null;
  /**
   * Readiness, 0–100, when the topics are ranked by it (ORA-1101). Not a
   * percentage.
   */
  readiness?: number | null;
}

/** Which number ordered the dashboard's topic lists (ORA-1101). */
export type ChatDashboardTopicMetric = 'readiness' | 'accuracy';

export interface ChatDashboardBlockPayload {
  /** Which scope the block opens on. */
  view: 'exam' | 'global';
  examName?: string | null;
  qbank?: {
    accuracy?: number | null;
    completed?: number | null;
    correct?: number | null;
    recentAccuracy?: number | null;
    recentTotal?: number | null;
  } | null;
  weakTopics?: ChatDashboardTopic[];
  strongTopics?: ChatDashboardTopic[];
  /**
   * Set only while the server may rank this student's topics by readiness
   * (ORA-1101); `accuracy` when no topic had enough readiness data. Absent,
   * the topics are labelled by accuracy as before.
   */
  metric?: ChatDashboardTopicMetric;
}

/**
 * A lecture document. No serve tool emits this in V1 — it exists because
 * legacy conversations restored read-only can contain one, and because document
 * chat will want it.
 */
export interface ChatDocumentBlockPayload {
  lectureId: string;
  fileName?: string | null;
}

export type ChatFileBlockFormat = 'pdf' | 'docx' | 'csv';

export type ChatFileBlockStatus =
  'ready_to_build' | 'building' | 'ready' | 'failed';

/**
 * A private export generated from the student's companion conversation.
 *
 * The payload deliberately carries no URL. Download URLs last only ten minutes,
 * so the block asks the owner-checked endpoint for a fresh one on every tap.
 */
export interface ChatFileBlockPayload {
  exportId: string;
  filename: string;
  format: ChatFileBlockFormat;
  status: ChatFileBlockStatus;
  title?: string | null;
  itemCount?: number;
  sourceLabel?: string | null;
  notes: string[];
}

/**
 * A page of a lecture document, added from a source pill (ORA-514): the cited
 * excerpt, with "Open at page N" taking the student to the viewer at that
 * page. The server builds it from the citation, so the excerpt is the same
 * bounded text the citation carried.
 */
export interface ChatLectureExcerptBlockPayload {
  source?: 'lecture';
  lectureId: string;
  page?: number | null;
  /** The document's display name. */
  title?: string | null;
  excerpt?: string | null;
}

/**
 * mcgraw-hill: a verbatim passage of a chat document page, placed inline by
 * the `quote_reference` tool. The server verified the excerpt against the
 * page, and the card opens the page in the side viewer.
 */
export interface ChatDocumentQuoteExcerptBlockPayload {
  source: 'chat_document';
  documentId: string;
  page: number;
  title?: string | null;
  excerpt: string;
}

export type ChatDocumentExcerptBlockPayload =
  ChatLectureExcerptBlockPayload | ChatDocumentQuoteExcerptBlockPayload;

/** A server-authorized destination offered by Ora in the conversation. */
export interface ChatNavigationBlockPayload {
  /** The concise action label shown on the card. */
  label: string;
  /** Optional context explaining why this destination helps. */
  description?: string | null;
}

/**
 * A study set: a handful of items served inline in the thread, with the rest of
 * the same filter one tap away in the full study UI (ORA-677). The kind says
 * which study surface owns it; the payload is written by the server and the
 * block renders from it alone.
 */
export type ChatStudySetKind = 'question' | 'flashcard' | 'video';

/**
 * Where the set is in its life: `served` (sitting in the thread), `launched`
 * (the student opened the full session at least once) and `completed` (every
 * item has a completion).
 */
export type ChatStudySetStatus = 'served' | 'launched' | 'completed';

/** How far through the set the student is; `correct` is question sets only. */
export interface ChatStudySetProgress {
  done: number;
  total: number;
  correct?: number;
}

/**
 * The daily allowance as it stood when a question set was served, in the same
 * shape a single served question carries it.
 */
export type ChatStudySetLimits = ChatQuestionBlockLimits;

/**
 * One flashcard of an inline set: the *presented* variant's text, the variant
 * it came from, and the student's scheduling for the card — which is what makes
 * an in-chat rating a real review rather than a preview (the deck computes the
 * next interval from it with the same FSRS path the study surface uses).
 * `scheduling` is null for a card the student has never seen.
 */
export interface ChatStudySetFlashcard extends ChatFlashcardBlockCard {
  variantId: string;
  /**
   * How many variants the card has. The block is served one presented variant
   * rather than the whole list, so the count comes over the wire instead: it is
   * what lets the in-chat FSRS step walk `difficultyIndex` exactly as the full
   * study surface does. 1 when the server did not say.
   */
  variantCount: number;
  scheduling: FlashcardScheduling | null;
}

/**
 * One video of an inline set: the video block payload plus the two facts the
 * set's rail draws on first paint (ORA-686).
 *
 * Not on `ChatVideoBlockPayload` itself — a single served video renders one
 * player with its own chrome and has no tile to print a duration under, so the
 * server does not pay for either read there.
 */
export interface ChatStudySetVideo extends ChatVideoBlockPayload {
  /** The playable version's length. Null when the server does not know it. */
  durationSeconds: number | null;
  /** How far this student got, 0-100. 100 once the item is done. */
  progressPercent: number;
}

/**
 * One item of an inline set, in the order the server put them in. `done` is the
 * server's view at serve time: an item the student completes in the thread is
 * marked done locally until the block is served again.
 */
export type ChatStudySetItem =
  | {
      ord: number;
      done: boolean;
      kind: 'question';
      question: ChatQuestionBlockPayload;
    }
  | {
      ord: number;
      done: boolean;
      kind: 'flashcard';
      card: ChatStudySetFlashcard;
    }
  | { ord: number; done: boolean; kind: 'video'; video: ChatStudySetVideo };

/** Where the footer's one button opens the set. */
export interface ChatStudySetLaunch {
  route:
    | 'study.qbank.block'
    | 'study.flashcards'
    | 'study.video'
    | 'study.qbank.review';
  params: { setId: string; blockId?: string };
}

export interface ChatStudySetBlockPayload {
  setId: string;
  kind: ChatStudySetKind;
  status: ChatStudySetStatus;
  /** A lazy set's asked-for count; an inline set's `items.length`. */
  requestedCount: number;
  /** What the filter yielded when the set was made, when the server knows. */
  poolCount: number | null;
  /** The inline threshold for this kind: above it the set is lazy. */
  inlineMax: number;
  /** Server-written, e.g. "24 due Renal cards". Never model-authored. */
  scopeLabel: string;
  progress: ChatStudySetProgress;
  /** Present ONLY for inline sets; a lazy set has nothing to draw in-thread. */
  items?: ChatStudySetItem[];
  limits?: ChatStudySetLimits | null;
  launch: ChatStudySetLaunch;
  /** Present when the pool is bigger than what was served. */
  moreSession?: { count: number };
}

/**
 * The server-authored row written when a student comes back from a set: a quiet
 * one-line event under the thread rather than a card, and excluded from the
 * per-turn block cap because it is not content the turn served.
 */
export interface ChatSetResultBlockPayload {
  setId: string;
  kind: ChatStudySetKind;
  status: ChatStudySetStatus;
  progress: ChatStudySetProgress;
  scopeLabel: string;
}

/**
 * mcgraw-hill: a figure of a reference document shown inside an answer: its
 * label ("FIGURE 265-3"), the document, the image (when one is available),
 * the caption, and the page "Open chapter" goes to.
 */
export interface McGrawHillFigureBlockPayload {
  documentId: string;
  page: number;
  /** "FIGURE 265-3". */
  label: string;
  /** The document's title. */
  title: string;
  caption: string;
  /**
   * The backend's reference figure id (members-only image); without one, or
   * until it loads, the card shows a placeholder.
   */
  figureId?: string | null;
}

/**
 * mcgraw-hill: a scripted answer's action row: a button that opens a
 * reference document at a page, and Copy for the answer's text.
 */
export interface McGrawHillActionsBlockPayload {
  open?: {
    /** "Open in Harrison's". */
    label: string;
    documentId: string;
    page: number;
    /** What the page highlights when it opens. */
    highlightText?: string;
  } | null;
  /** The answer as plain text, for Copy answer. */
  copyText: string;
}
